import { MetadataFormData } from "@saleor/components/Metadata";
import { SingleAutocompleteChoiceType } from "@saleor/components/SingleAutocompleteSelectField";
import useForm, { FormChange } from "@saleor/hooks/useForm";
import useFormset, {
  FormsetChange,
  FormsetData
} from "@saleor/hooks/useFormset";
import { GetErpProductVariant_erpProductVariant } from "@saleor/products/types/GetErpProductVariant";
import { ProductVariantCreateData_product } from "@saleor/products/types/ProductVariantCreateData";
import { getVariantAttributeInputFromProduct } from "@saleor/products/utils/data";
import { SearchWarehouses_search_edges_node } from "@saleor/searches/types/SearchWarehouses";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import React from "react";

import { ProductStockInput } from "../ProductStocks";
import { VariantAttributeInputData } from "../ProductVariantAttributes";

export interface ProductVariantCreateFormData extends MetadataFormData {
  costPrice: string;
  price: string;
  sku: string;
  trackInventory: boolean;
  weight: string;
  cornermark: string;
  extraChargeCategory: string;
}
export interface ProductVariantCreateData extends ProductVariantCreateFormData {
  attributes: FormsetData<VariantAttributeInputData, string>;
  stocks: ProductStockInput[];
}

export interface UseProductVariantCreateFormOpts
  extends Record<"cornermarks", SingleAutocompleteChoiceType[]> {
  setSelectedCornermark: React.Dispatch<React.SetStateAction<string>>;
  setSelectedExtracharge: React.Dispatch<React.SetStateAction<string>>;
  warehouses: SearchWarehouses_search_edges_node[];
}

export interface UseProductVariantCreateFormResult {
  change: FormChange;
  data: ProductVariantCreateData;
  // TODO: type FormsetChange
  handlers: Record<"changeStock" | "selectAttribute", FormsetChange> &
    Record<"addStock" | "deleteStock", (id: string) => void> & {
      changeMetadata: FormChange;
      selectCornermark: FormChange;
      selectExtracharge: FormChange;
    };
  hasChanged: boolean;
  submit: () => void;
}

export interface ProductVariantCreateFormProps
  extends UseProductVariantCreateFormOpts {
  children: (props: UseProductVariantCreateFormResult) => React.ReactNode;
  product: ProductVariantCreateData_product;
  erpProductVariant: GetErpProductVariant_erpProductVariant;
  onSubmit: (data: ProductVariantCreateData) => void;
}

function useProductVariantCreateForm(
  product: ProductVariantCreateData_product,
  erpProductVariant: GetErpProductVariant_erpProductVariant,
  onSubmit: (data: ProductVariantCreateData) => void,
  opts: UseProductVariantCreateFormOpts
): UseProductVariantCreateFormResult {
  const [changed, setChanged] = React.useState(false);
  const triggerChange = () => setChanged(true);

  const attributeInput = getVariantAttributeInputFromProduct(
    product,
    erpProductVariant
  );

  const initial: ProductVariantCreateFormData = {
    costPrice: "",
    metadata: [],
    price: "",
    privateMetadata: [],
    sku: "",
    trackInventory: true,
    weight: erpProductVariant?.weight.toString() || "",
    cornermark: null,
    extraChargeCategory: ""
  };

  const form = useForm(initial);
  const attributes = useFormset(attributeInput);
  const stocks = useFormset<null, string>([]);
  const {
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  const handleChange: FormChange = (event, cb) => {
    form.change(event, cb);
    triggerChange();
  };
  const changeMetadata = makeMetadataChangeHandler(handleChange);
  const handleAttributeChange: FormsetChange = (id, value) => {
    attributes.change(id, value);
    triggerChange();
  };
  const handleCornermarkSelect = createSingleAutocompleteSelectHandler(
    handleChange,
    opts.setSelectedCornermark,
    opts.cornermarks
  );
  const handleExtrachargeSelect = createSingleAutocompleteSelectHandler(
    handleChange,
    opts.setSelectedExtracharge,
    opts.cornermarks
  );

  const handleStockAdd = (id: string) => {
    triggerChange();
    stocks.add({
      data: null,
      id,
      label: opts.warehouses.find(warehouse => warehouse.id === id).name,
      value: "0"
    });
  };
  const handleStockChange = (id: string, value: string) => {
    triggerChange();
    stocks.change(id, value);
  };
  const handleStockDelete = (id: string) => {
    triggerChange();
    stocks.remove(id);
  };

  const data: ProductVariantCreateData = {
    ...form.data,
    attributes: attributes.data,
    stocks: stocks.data
  };

  const submit = () => onSubmit(data);

  return {
    change: handleChange,
    data,
    handlers: {
      addStock: handleStockAdd,
      changeMetadata,
      changeStock: handleStockChange,
      deleteStock: handleStockDelete,
      selectAttribute: handleAttributeChange,
      selectCornermark: handleCornermarkSelect,
      selectExtracharge: handleExtrachargeSelect
    },
    hasChanged: changed,
    submit
  };
}

const ProductVariantCreateForm: React.FC<ProductVariantCreateFormProps> = ({
  children,
  product,
  erpProductVariant,
  onSubmit,
  ...rest
}) => {
  const props = useProductVariantCreateForm(
    product,
    erpProductVariant,
    onSubmit,
    rest
  );

  return <form onSubmit={props.submit}>{children(props)}</form>;
};

ProductVariantCreateForm.displayName = "ProductVariantCreateForm";
export default ProductVariantCreateForm;
